import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';

import { populateInputFields } from 'Scripts/Slices/newsletter';
import { IRootState } from 'Scripts/store';

import { useNewsletterSignupMutation } from 'Services/newsletterApi';
import { newsletterSignupDataLayerPush } from 'Utils/dataLayerPushService';

import { INewsletterSignupBlockViewModel } from 'Types/Litium';
import CtaButton from 'Components/CtaButton';
import {
    handleFormPost,
    validateFormFields,
    ValidationSchema,
} from 'Components/Forms/RenderFormField';
import Markup from 'Components/Markup';
import NewsLetterInputField from 'Components/NewsLetterBlock/NewsLetterInputField';
import { setVoyadoContactId } from 'Services/voyadoApi';

const NewsletterSignup = ({
    header,
    backgroundImage,
    termsAndConditionText,
    inputFields,
    submitText,
    confirmationText,
}: INewsletterSignupBlockViewModel) => {
    const [formValidation, setFormValidation] = useState<ValidationSchema>([]);
    const dispatch = useDispatch();

    const [
        newsletterSignup,
        {
            isLoading: newsletterSignupResultIsLoading,
            error: newsletterSignupResultError,
            data: newsletterSignupResultData,
            isSuccess: newsletterSignupResultIsSuccess,
        },
    ] = useNewsletterSignupMutation();
    const handleOnsubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (
            validateFormFields(
                newsletterSignupInputFields.filter(
                    x => x.name === inputFields[0].name
                ),
                setFormValidation
            )
        ) {
            newsletterSignup({ form: e.currentTarget });
            newsletterSignupDataLayerPush(
                window.__litium.dataLayerEvents.newsletterSignupEventTrigger
            );
        }
    };
    const newsletterSignupInputFields = useSelector(
        (state: IRootState) => state.newsletter.inputFields
    );

    useEffect(() => {
        dispatch(populateInputFields(inputFields[0]));
    }, []);

    useEffect(() => {
        handleFormPost(newsletterSignupResultData, newsletterSignupResultError);

        if (newsletterSignupResultIsSuccess) {
            toast.success(confirmationText);

            if(newsletterSignupResultData && newsletterSignupResultData.contactId) {
                setVoyadoContactId(newsletterSignupResultData.contactId);
            }
        }
    }, [newsletterSignupResultData, newsletterSignupResultError]);

    const error = formValidation.find(
        element => element.name === inputFields[0].name
    );

    return (
        <div
            style={{
                backgroundImage:
                    backgroundImage?.src &&
                    `url(${backgroundImage.src}?format=webp)`,
            }}
            className="flex h-auto w-full bg-white bg-cover bg-center bg-no-repeat py-8"
        >
            <div className="flex w-full justify-center px-4">
                <form onSubmit={handleOnsubmit}>
                    <fieldset className="flex w-full flex-col items-center">
                        <legend className="mb-3 w-full font-montserrat text-xl lg:w-[36rem] lg:text-2xl">
                            {header}
                        </legend>
                        <div className="w-full lg:w-[36rem]">
                            <div className="flex w-full flex-col lg:flex-row lg:gap-4">
                                <NewsLetterInputField
                                    field={
                                        newsletterSignupInputFields.find(
                                            x => x.name === inputFields[0].name
                                        ) || inputFields[0]
                                    }
                                    loading={newsletterSignupResultIsLoading}
                                    error={error}
                                />
                                <div className="w-full lg:w-auto">
                                    <CtaButton
                                        className="mb-2 h-[46px] w-full border-0 bg-woodland text-lg lg:mb-0 lg:w-auto"
                                        type="submit"
                                    >
                                        {submitText}
                                    </CtaButton>
                                </div>
                            </div>

                            <Markup
                                html={termsAndConditionText?.value}
                                cssClass="m-0"
                            />
                        </div>
                    </fieldset>
                </form>
            </div>
        </div>
    );
};

export default NewsletterSignup;
